import { Route } from '@angular/router';
import { CustomProductList } from '@catalog/const';
import { checkoutV2Guard } from '@checkout-v2/utils';
import { CustomPageType } from '@core/const';
import { DeviceDispatchGuard, LoginGuard, NotProdGuard } from '@core/feature';
import { ProductNotFoundGuard, productRecommendationResolver } from '@product/feature/v2';
import { promoResolver } from '@promo/feature';
import { DeprecatedSearchParamsGuard } from '@search/feature';
import { seoPageResolver } from '@seo/feature';
import { AuthGuard } from '@spartacus/core';
import { CmsPageGuard } from '@spartacus/storefront';
import { LOGIN_ROOT_PATH, LOGIN_TYPES } from '@user/const';

export const routes: Route[] = [
  {
    path: '/',
    pathMatch: 'full',
    loadChildren: () => import('@home/shell').then((m) => m.HomeShellModule),
    resolve: {
      promo: promoResolver,
      productRecommendation: productRecommendationResolver,
      seoPage: seoPageResolver('home'),
    },
    data: { semanticName: 'homepage', cxRoute: 'home' },
  },
  {
    path: 'menu',
    loadComponent: () => import('./lazy-reexports/mobile-menu').then((m) => m.MobileMenuComponent),
    data: { semanticName: 'menu' },
  },
  {
    path: 'catalog',
    children: [
      {
        path: '',
        loadComponent: () => import('./lazy-reexports/catalog').then((m) => m.CatalogComponent),
      },
      {
        path: ':categoryCode',
        loadComponent: () => import('./lazy-reexports/catalog').then((m) => m.CatalogDetailsComponent),
      },
    ],
    resolve: { seoPage: seoPageResolver('catalog') },
    data: { semanticName: 'catalog' },
  },
  {
    path: LOGIN_ROOT_PATH,
    children: [
      {
        path: '',
        redirectTo: LOGIN_TYPES.CALL,
        pathMatch: 'full',
      },
      {
        path: LOGIN_TYPES.CALL,
        data: { semanticName: LOGIN_TYPES.CALL },
        loadComponent: () => import('./lazy-reexports/login-container').then((m) => m.LoginContainerComponent),
      },
      {
        path: LOGIN_TYPES.CODE,
        data: { semanticName: LOGIN_TYPES.CODE },
        loadComponent: () => import('./lazy-reexports/login-container').then((m) => m.LoginContainerComponent),
      },
      {
        path: LOGIN_TYPES.PASSWORD,
        data: { semanticName: LOGIN_TYPES.PASSWORD },
        loadComponent: () => import('./lazy-reexports/login-container').then((m) => m.LoginContainerComponent),
      },
    ],
    resolve: { seoPage: seoPageResolver('login') },
    canActivate: [LoginGuard],
    data: { semanticName: 'login' },
  },
  {
    path: 'contacts',
    loadComponent: () => import('./lazy-reexports/contacts').then((m) => m.ContactsPageComponent),
    resolve: { seoPage: seoPageResolver('contacts') },
    data: { semanticName: 'contacts' },
  },
  {
    canActivate: [CmsPageGuard],
    loadComponent: () => import('./lazy-reexports/category-page').then((m) => m.CategoryPageComponent),
    data: {
      semanticName: 'category',
      cxRoute: 'category',
    },
  },
  {
    canActivate: [CmsPageGuard],
    loadComponent: () => import('./lazy-reexports/promo-campaign-page').then((m) => m.PromoCampaignPageComponent),
    data: {
      semanticName: 'promo-campaign',
      cxRoute: CustomProductList.PromoProductList,
      customCmsRouteContext: { type: CustomPageType.CampaignPage },
    },
  },
  {
    canActivate: [CmsPageGuard],
    loadComponent: () => import('./lazy-reexports/category-page').then((m) => m.CategoryPageComponent),
    data: {
      semanticName: 'tag',
      cxRoute: CustomProductList.SeoProductList,
      customCmsRouteContext: { type: CustomPageType.TagPage },
    },
  },
  {
    canActivate: [CmsPageGuard, DeprecatedSearchParamsGuard],
    loadComponent: () => import('./lazy-reexports/search-page').then((m) => m.SearchPageComponent),
    data: {
      semanticName: 'search',
      cxRoute: 'search',
      pageLabel: 'search',
    },
  },
  {
    canActivate: [CmsPageGuard],
    loadComponent: () => import('./lazy-reexports/product-page').then((m) => m.ProductPageComponent),
    data: { semanticName: 'product', cxRoute: 'product' },
    resolve: {
      productRecommendation: productRecommendationResolver,
    },
  },
  {
    path: 'reviews/:productCode',
    loadComponent: () => import('./lazy-reexports/product-reviews-page').then((m) => m.ProductReviewsPageComponent),
    resolve: { seoPage: seoPageResolver('reviews') },
    data: { semanticName: 'productReviews' },
    canActivate: [ProductNotFoundGuard],
  },
  {
    path: 'cart',
    data: { cxRoute: 'cart', semanticName: 'cart' },
    loadChildren: () => import('@cart/shell').then((m) => m.CartShellModule),
    resolve: {
      seoPage: seoPageResolver('cart'),
      productRecommendation: productRecommendationResolver,
    },
  },
  {
    path: 'sandbox',
    canActivate: [NotProdGuard],
    loadComponent: () => import('./lazy-reexports/sandbox-component').then((m) => m.SandboxComponent),
  },
  {
    path: 'get-app',
    canActivate: [DeviceDispatchGuard, CmsPageGuard],
    loadComponent: () => import('./lazy-reexports/storefront-page-layout').then((m) => m.PageLayoutComponent),
  },
  {
    path: 'my-account',
    canMatch: [AuthGuard],
    loadChildren: () => import('@my-account/shell').then((m) => m.MyAccountShellModule),
    data: { semanticName: 'my-account' },
  },
  {
    // checkout v2
    path: 'checkout',
    canMatch: [AuthGuard, checkoutV2Guard],
    loadChildren: () => import('@checkout-v2/shell').then((m) => m.CheckoutShellModule),
    data: { semanticName: 'checkout' },
  },
  {
    // checkout v1
    path: 'checkout',
    canMatch: [AuthGuard],
    loadChildren: () => import('@checkout/shell').then((m) => m.CheckoutShellModule),
    data: { semanticName: 'checkout' },
  },
  {
    path: 'payment-result',
    loadChildren: () => import('@payment-result/shell').then((m) => m.PaymentResultShellModule),
    data: { semanticName: 'payment-result' },
  },
  {
    path: 'not-found',
    loadComponent: () => import('@main/navigation/ui/v2').then((m) => m.NotFoundComponent),
    data: { cxRoute: 'notFound', semanticName: 'notFound' },
  },
];
